import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, BookOpen, CalendarDays, FileText, Users, Info } from "lucide-react";
import { toast } from "sonner";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { WfButton, CreditChip } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * FarmerPortalPage — sign-in shell for the upcoming Farmer Portal.
 * NOT FUNCTIONAL YET: the form validates locally and shows a "coming soon"
 * notice. Backend authentication will be wired when the portal is built.
 */

const PORTAL = {
  eyebrow: "Farmer Portal",
  title: "Welcome back, farmer",
  lede:
    "Sign in to access training materials, market schedules, farm announcements, and important documents \u2014 all in one place.",
  image: "/images/wf/vlai/vlai-3480.jpg",
  imageAlt: "A farmer crouches inside a high tunnel, harvesting leafy greens.",
  perks: [
    { icon: BookOpen, title: "Training materials", body: "Workshop handouts, videos, and guides in multiple languages." },
    { icon: CalendarDays, title: "Market & farm schedules", body: "Your market days, deliveries, and farm meeting dates." },
    { icon: FileText, title: "Important documents", body: "Plot agreements, food-safety forms, and grant paperwork." },
    { icon: Users, title: "Farmer community", body: "Announcements and updates from World Farmers staff." },
  ],
};

export default function FarmerPortalPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Please enter a valid email address.";
    if (!password) next.password = "Please enter your password.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setNotice("The Farmer Portal is not open yet. Sign-in will be available when the portal launches.");
    toast("Farmer Portal coming soon", {
      description: "Sign-in isn\u2019t active yet. Please contact World Farmers staff for training materials in the meantime.",
    });
  };

  return (
    <>
      <SkipToContent />
      <StickyHeader linkToHome />
      <main id="content" tabIndex={-1} className="outline-none bg-[var(--wf-cream)]">
        <section aria-labelledby="portal-heading" className="py-12 sm:py-16 lg:py-20">
          <div className="wf-container">
            <div className="grid grid-cols-1 overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow)] lg:grid-cols-12">
              {/* Visual / perks */}
              <div className="relative min-h-[18rem] lg:col-span-6 lg:min-h-[40rem]">
                <img src={PORTAL.image} alt={PORTAL.imageAlt} loading="eager" className="absolute inset-0 h-full w-full object-cover" />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
                <div className="relative flex h-full flex-col justify-end p-8 sm:p-10">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[rgba(255,253,248,0.85)]">
                    <span aria-hidden="true" className="h-px w-8 bg-[rgba(255,253,248,0.6)]" />
                    {PORTAL.eyebrow}
                  </span>
                  <p className="mt-4 max-w-md font-serif text-2xl font-semibold leading-tight text-white sm:text-3xl">Everything you need for the season, in one place.</p>
                  <ul className="mt-6 hidden grid-cols-2 gap-4 sm:grid">
                    {PORTAL.perks.map((p) => {
                      const Icon = p.icon;
                      return (
                        <li key={p.title} className="rounded-sm border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                          <Icon className="h-5 w-5 text-[var(--wf-wheat)]" aria-hidden="true" />
                          <p className="mt-2 text-sm font-semibold text-white">{p.title}</p>
                          <p className="mt-1 text-xs leading-5 text-white/80">{p.body}</p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <CreditChip src={PORTAL.image} className="absolute right-3 top-3 rounded-full bg-black/45 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" />
              </div>

              {/* Form */}
              <div className="flex flex-col justify-center p-8 sm:p-10 lg:col-span-6 lg:p-14">
                <div className="inline-flex items-center gap-2 self-start rounded-full border border-[var(--wf-border)] bg-[var(--wf-wheat)]/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--wf-maroon)]" data-testid="portal-coming-soon-badge">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" /> Coming soon
                </div>
                <h1 id="portal-heading" data-testid="portal-headline" className="mt-5 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">
                  {PORTAL.title}
                </h1>
                <p className="mt-3 max-w-md text-base leading-7 text-[var(--wf-soil)]">{PORTAL.lede}</p>

                <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5" data-testid="portal-signin-form" aria-describedby="portal-status">
                  <div className="space-y-2">
                    <Label htmlFor="portal-email" className="text-sm font-semibold text-[var(--wf-ink)]">Email address</Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                      <Input
                        id="portal-email"
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        aria-invalid={errors.email ? "true" : "false"}
                        aria-describedby={errors.email ? "portal-email-error" : undefined}
                        data-testid="portal-email-input"
                        className="min-h-11 rounded-lg border-[var(--wf-border)] bg-white pl-9 focus-visible:ring-[var(--wf-maroon)]"
                      />
                    </div>
                    {errors.email && <p id="portal-email-error" role="alert" data-testid="portal-email-error" className="text-sm text-[var(--wf-maroon)]">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="portal-password" className="text-sm font-semibold text-[var(--wf-ink)]">Password</Label>
                      <button type="button" onClick={() => toast("Password reset will be available when the portal launches.")} data-testid="portal-forgot-link" className="text-xs font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                      <Input
                        id="portal-password"
                        type={showPw ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        aria-invalid={errors.password ? "true" : "false"}
                        aria-describedby={errors.password ? "portal-password-error" : undefined}
                        data-testid="portal-password-input"
                        className="min-h-11 rounded-lg border-[var(--wf-border)] bg-white pl-9 pr-11 focus-visible:ring-[var(--wf-maroon)]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        aria-pressed={showPw}
                        data-testid="portal-toggle-password"
                        className="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-[var(--wf-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {showPw ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                      </button>
                    </div>
                    {errors.password && <p id="portal-password-error" role="alert" data-testid="portal-password-error" className="text-sm text-[var(--wf-maroon)]">{errors.password}</p>}
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="portal-remember" checked={remember} onCheckedChange={(v) => setRemember(Boolean(v))} data-testid="portal-remember-checkbox" className="border-[var(--wf-border)] data-[state=checked]:bg-[var(--wf-maroon)] data-[state=checked]:border-[var(--wf-maroon)]" />
                    <Label htmlFor="portal-remember" className="text-sm font-medium text-[var(--wf-soil)]">Keep me signed in on this device</Label>
                  </div>

                  <WfButton type="submit" withArrow className="w-full " data-testid="portal-signin-button">
                    Sign in
                  </WfButton>

                  <div id="portal-status" role="status" aria-live="polite" data-testid="portal-status" className={notice ? "flex items-start gap-3 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-wheat)]/60 p-4 text-sm leading-6 text-[var(--wf-ink)]" : "sr-only"}>
                    {notice && <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-maroon)]" aria-hidden="true" />}
                    {notice}
                  </div>
                </form>

                <p className="mt-8 text-sm leading-6 text-muted-foreground">
                  The Farmer Portal is under construction. Farmers at the Flats will receive login details from World Farmers staff when it launches. Questions?{" "}
                  <Link to="/contact" data-testid="portal-contact-link" className="font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Contact us</Link>.
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Staff or board member?{" "}
                  <Link to="/admin" data-testid="portal-staff-link" className="font-semibold text-[var(--wf-green-text)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Use the staff login</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
